import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ProjectWorkflowFoundationApi from "../../../../../infrastructure/api/projectWorkflowFoundation";
import WorkspaceCustomerContractPanel from "./WorkspaceCustomerContractPanel";
import WorkspaceFinalApprovalPanel from "./WorkspaceFinalApprovalPanel";

const formatMoney = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num) || num <= 0) return null;
  return `${num.toLocaleString("fa-IR")} ریال`;
};

const resolveCustomerContractChipLabel = (gate) =>
  gate?.hasCustomerContractApproved
    ? "قرارداد مشتری — تأیید شده"
    : "قرارداد مشتری — در انتظار تأیید";

const resolveExecutionContractChipLabel = (gate) => {
  if (!gate?.hasActiveProjectContract) return "قرارداد اجرا — ثبت نشده";
  if (gate?.hasFinalApproval) return "قرارداد اجرا — تأیید شده";
  return "قرارداد اجرا — در انتظار";
};

const resolveInvoiceChipLabel = (gate) => {
  if (!gate?.financialGateRequired) return null;
  if ((gate?.requiredInvoiceCount ?? 0) === 0) return "فاکتور — ثبت نشده";
  if (gate?.isSatisfied) return "فاکتور — تسویه شده";
  return "فاکتور — در انتظار پرداخت";
};

const resolvePaymentChipLabel = (gate) => {
  if (!gate?.financialGateRequired) return "پرداخت — غیرفعال";
  if (gate?.isSatisfied && gate?.canActivateExecution) return "پرداخت — آماده فعال‌سازی";
  if (gate?.isSatisfied) return "پرداخت — آماده فعال‌سازی";
  return "پرداخت — غیرفعال";
};

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const normalizeGate = (raw) => {
  if (!raw) return null;
  return {
    financialGateRequired:
      pick(raw, "financialGateRequired", "FinancialGateRequired") ?? false,
    isSatisfied: pick(raw, "isSatisfied", "IsSatisfied") ?? false,
    blockingReason: pick(raw, "blockingReason", "BlockingReason") ?? "",
    requiredInvoiceAmount:
      pick(raw, "requiredInvoiceAmount", "RequiredInvoiceAmount") ?? 0,
    paidAmount: pick(raw, "paidAmount", "PaidAmount") ?? 0,
    dueAmount: pick(raw, "dueAmount", "DueAmount") ?? 0,
    unpaidInvoiceCount: pick(raw, "unpaidInvoiceCount", "UnpaidInvoiceCount") ?? 0,
    partialInvoiceCount: pick(raw, "partialInvoiceCount", "PartialInvoiceCount") ?? 0,
    canActivateExecution:
      pick(raw, "canActivateExecution", "CanActivateExecution") ?? false,
    activationBlockReason:
      pick(raw, "activationBlockReason", "ActivationBlockReason") ?? "",
    hasFinalApproval: pick(raw, "hasFinalApproval", "HasFinalApproval") ?? false,
    hasCustomerContractApproved:
      pick(raw, "hasCustomerContractApproved", "HasCustomerContractApproved") ?? false,
    hasActiveProjectContract:
      pick(raw, "hasActiveProjectContract", "HasActiveProjectContract") ?? false,
    requiredInvoiceCount:
      pick(raw, "requiredInvoiceCount", "RequiredInvoiceCount") ?? 0,
    paymentScheduleApplied:
      pick(raw, "paymentScheduleApplied", "PaymentScheduleApplied") ?? false,
    preExecutionPaymentRequired:
      pick(raw, "preExecutionPaymentRequired", "PreExecutionPaymentRequired") ?? 0,
    postDeliveryPaymentAmount:
      pick(raw, "postDeliveryPaymentAmount", "PostDeliveryPaymentAmount") ?? 0,
    customerContributionPaid:
      pick(raw, "customerContributionPaid", "CustomerContributionPaid") ?? 0,
    postDeliveryPaymentDue:
      pick(raw, "postDeliveryPaymentDue", "PostDeliveryPaymentDue") ?? 0,
    preExecutionPaymentSatisfied:
      pick(raw, "preExecutionPaymentSatisfied", "PreExecutionPaymentSatisfied") ?? false,
    postDeliveryDeferralApproved:
      pick(raw, "postDeliveryDeferralApproved", "PostDeliveryDeferralApproved") ?? false,
    canApprovePostDeliveryDeferral:
      pick(raw, "canApprovePostDeliveryDeferral", "CanApprovePostDeliveryDeferral") ?? false,
    postDeliveryDeferralNotes:
      pick(raw, "postDeliveryDeferralNotes", "PostDeliveryDeferralNotes") ?? "",
  };
};

const WorkspaceExecutionSection = ({
  requestId,
  source,
  orchestratorLinks,
  onProgressChanged,
  onGoToFinancial,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);
  const [error, setError] = useState("");
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [deferralConfirmOpen, setDeferralConfirmOpen] = useState(false);
  const [deferralNotes, setDeferralNotes] = useState("");
  const [deferralSubmitting, setDeferralSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError("");
    try {
      const res = await ProjectWorkflowFoundationApi.getProjectFinancialExecutionGateStatus(
        source,
        requestId
      );
      if (!res.ok) {
        setError(res.message || "خطا در دریافت وضعیت گیت مالی");
        setGate(null);
      } else {
        setGate(normalizeGate(res.data));
      }
    } catch {
      setError("خطا در دریافت وضعیت گیت مالی");
      setGate(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, source]);

  useEffect(() => {
    load();
  }, [load]);

  const canActivate = gate?.canActivateExecution === true;
  const prerequisitesMet =
    gate?.hasCustomerContractApproved &&
    gate?.hasActiveProjectContract &&
    gate?.hasFinalApproval;
  const needsInvoice =
    prerequisitesMet &&
    gate?.financialGateRequired &&
    !gate?.isSatisfied &&
    (gate?.requiredInvoiceCount ?? 0) === 0;
  const needsPayment =
    prerequisitesMet &&
    gate?.financialGateRequired &&
    !gate?.isSatisfied &&
    (gate?.requiredInvoiceCount ?? 0) > 0;
  const blockMessage =
    gate?.activationBlockReason ||
    gate?.blockingReason ||
    "برای فعال‌سازی اجرا، ابتدا پرداخت فاکتورهای الزامی باید تکمیل شود.";

  const refreshAll = useCallback(() => {
    load();
    onProgressChanged?.();
  }, [load, onProgressChanged]);

  const handleApprovePostDeliveryDeferral = async () => {
    setDeferralSubmitting(true);
    try {
      const res = await ProjectWorkflowFoundationApi.approvePostDeliveryPaymentDeferral(
        source,
        { requestId: Number(requestId), notes: deferralNotes.trim() || null }
      );
      if (!res.ok) {
        toast.error(res.message || "تأیید پرداخت پس از تحویل ناموفق بود.");
        return;
      }
      toast.success(res.message || "پرداخت پس از تحویل تأیید شد.");
      setDeferralConfirmOpen(false);
      setDeferralNotes("");
      refreshAll();
    } catch {
      toast.error("تأیید پرداخت پس از تحویل ناموفق بود.");
    } finally {
      setDeferralSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <WorkspaceCustomerContractPanel
        requestId={requestId}
        source={source}
        onChanged={refreshAll}
      />

      <Box id="workspace-final-approval">
        <WorkspaceFinalApprovalPanel
          requestId={requestId}
          source={source}
          hasFinalApproval={gate?.hasFinalApproval === true}
          onChanged={refreshAll}
        />
      </Box>

      <Alert severity="info">
        فعال‌سازی اجرا پس از تأیید قرارداد مشتری، تأیید نهایی و گیت وام (در صورت نیاز) مجاز است.
        طبق قواعد سولارنت، سهم آورده مشتری به دو بخش تقسیم می‌شود: ۹۰٪ پیش از اجرا و ۱۰٪ پس از
        تحویل. اگر مشتری بخش پیش از اجرا را پرداخت کرده باشد، کارشناس می‌تواند ادامه پرداخت پس
        از تحویل را تأیید کند.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <AccountBalanceWalletIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>
            گیت مالی اجرا (پس از پرداخت فاکتور)
          </Typography>
          {loading ? <CircularProgress size={18} /> : null}
        </Stack>

        {error ? <Alert severity="warning">{error}</Alert> : null}

        {gate && !loading ? (
          <Stack spacing={1.5}>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip
                size="small"
                color={gate.isSatisfied ? "success" : "warning"}
                label={
                  gate.isSatisfied
                    ? "گیت مالی برآورده شد"
                    : "گیت مالی برآورده نشده"
                }
              />
              {gate.financialGateRequired ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    formatMoney(gate.dueAmount)
                      ? `مانده: ${formatMoney(gate.dueAmount)}`
                      : "مانده: مبلغ نهایی ثبت نشده"
                  }
                />
              ) : (
                <Chip size="small" variant="outlined" label="معاف از گیت فاکتور" />
              )}
              {formatMoney(gate.paidAmount) ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`پرداخت‌شده: ${formatMoney(gate.paidAmount)}`}
                />
              ) : null}
              {gate.unpaidInvoiceCount > 0 ? (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={`فاکتور پرداخت‌نشده: ${gate.unpaidInvoiceCount}`}
                />
              ) : null}
              {gate.paymentScheduleApplied ? (
                <>
                  <Chip
                    size="small"
                    color={gate.preExecutionPaymentSatisfied ? "success" : "warning"}
                    variant="outlined"
                    label={
                      formatMoney(gate.preExecutionPaymentRequired)
                        ? `پیش از اجرا: ${formatMoney(gate.preExecutionPaymentRequired)}`
                        : "پیش از اجرا"
                    }
                  />
                  <Chip
                    size="small"
                    color={gate.postDeliveryDeferralApproved ? "success" : "default"}
                    variant="outlined"
                    label={
                      formatMoney(gate.postDeliveryPaymentAmount)
                        ? `پس از تحویل: ${formatMoney(gate.postDeliveryPaymentAmount)}`
                        : "پس از تحویل"
                    }
                  />
                </>
              ) : null}
            </Stack>
            {gate.paymentScheduleApplied && formatMoney(gate.customerContributionPaid) ? (
              <Alert severity="info">
                پرداخت آورده مشتری: {formatMoney(gate.customerContributionPaid)}
                {formatMoney(gate.postDeliveryPaymentDue)
                  ? ` — مانده پس از تحویل: ${formatMoney(gate.postDeliveryPaymentDue)}`
                  : ""}
              </Alert>
            ) : null}
            {gate.postDeliveryDeferralApproved ? (
              <Alert severity="success">
                پرداخت پس از تحویل توسط کارشناس تأیید شده است.
                {gate.postDeliveryDeferralNotes ? ` (${gate.postDeliveryDeferralNotes})` : ""}
              </Alert>
            ) : null}
            {gate.canApprovePostDeliveryDeferral ? (
              <Alert severity="warning">
                مبلغ پیش از اجرا پرداخت شده است. برای فعال‌سازی اجرا، کارشناس باید پرداخت پس از
                تحویل ({formatMoney(gate.postDeliveryPaymentAmount)}) را تأیید کند.
              </Alert>
            ) : null}
            {!gate.isSatisfied && gate.blockingReason ? (
              <Alert severity="warning">{gate.blockingReason}</Alert>
            ) : null}
            {!gate.hasFinalApproval ? (
              <Alert severity="info">تایید نهایی پروژه هنوز ثبت نشده است.</Alert>
            ) : null}

            {gate.hasFinalApproval && !canActivate ? (
              <Alert severity="warning">
                {needsInvoice ? (
                  <>
                    تایید نهایی انجام شد. برای فعال‌سازی اجرا، ابتدا از بخش{" "}
                    <strong>مالی / پرداخت</strong> فاکتور قرارداد را صادر کنید، سپس
                    مشتری پرداخت را انجام دهد.
                  </>
                ) : needsPayment ? (
                  <>
                    {gate.canApprovePostDeliveryDeferral ? (
                      <>
                        مبلغ پیش از اجرا پرداخت شده؛ با تأیید کارشناس برای پرداخت پس از تحویل می‌توانید
                        فعال‌سازی اجرا را انجام دهید.
                      </>
                    ) : (
                      <>
                        فاکتور صادر شده است. پس از <strong>پرداخت کامل</strong> توسط مشتری،
                        دکمه فعال‌سازی اجرا باز می‌شود.
                        {gate.unpaidInvoiceCount > 0
                          ? ` (${gate.unpaidInvoiceCount} فاکتور پرداخت‌نشده)`
                          : ""}
                      </>
                    )}
                  </>
                ) : (
                  blockMessage ||
                  "همه پیش‌نیازهای تایید نهایی برقرار است؛ مانع دیگری برای فعال‌سازی وجود دارد."
                )}
              </Alert>
            ) : null}

            {(needsInvoice || needsPayment) && onGoToFinancial ? (
              <Button variant="contained" color="secondary" onClick={onGoToFinancial}>
                {needsInvoice ? "رفتن به مالی — صدور فاکتور" : "رفتن به مالی — وضعیت پرداخت"}
              </Button>
            ) : null}

            {gate.canApprovePostDeliveryDeferral ? (
              <Button
                variant="contained"
                color="warning"
                startIcon={<VerifiedIcon />}
                onClick={() => setDeferralConfirmOpen(true)}
              >
                تأیید پرداخت پس از تحویل
              </Button>
            ) : null}

            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              <Chip
                size="small"
                color={gate.hasCustomerContractApproved ? "success" : "default"}
                variant={gate.hasCustomerContractApproved ? "filled" : "outlined"}
                label={resolveCustomerContractChipLabel(gate)}
              />
              <Chip
                size="small"
                color={
                  gate.hasFinalApproval
                    ? "success"
                    : gate.hasActiveProjectContract
                      ? "warning"
                      : "default"
                }
                variant={gate.hasActiveProjectContract ? "filled" : "outlined"}
                label={resolveExecutionContractChipLabel(gate)}
              />
              <Chip
                size="small"
                color={gate.hasFinalApproval ? "success" : "default"}
                variant={gate.hasFinalApproval ? "filled" : "outlined"}
                label={
                  gate.hasFinalApproval ? "تأیید نهایی — ثبت شده" : "تأیید نهایی — ثبت نشده"
                }
              />
              {resolveInvoiceChipLabel(gate) ? (
                <Chip
                  size="small"
                  color={gate.isSatisfied ? "success" : "default"}
                  variant={gate.isSatisfied ? "filled" : "outlined"}
                  label={resolveInvoiceChipLabel(gate)}
                />
              ) : null}
              <Chip
                size="small"
                color={
                  gate.isSatisfied && gate.canActivateExecution
                    ? "success"
                    : "default"
                }
                variant={gate.isSatisfied ? "filled" : "outlined"}
                label={resolvePaymentChipLabel(gate)}
              />
            </Stack>
          </Stack>
        ) : null}
      </Paper>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              document
                .getElementById("workspace-final-approval")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            تایید نهایی پروژه
          </Button>
        </Grid>
        {[
          { label: "برنامه اجرا", href: orchestratorLinks.executionPlan },
          { label: "فعال‌سازی پرداخت", href: orchestratorLinks.paymentActivation },
          { label: "تامین تجهیزات", href: orchestratorLinks.procurementActivation },
          { label: "هماهنگی مایلستون", href: orchestratorLinks.milestoneCoordination },
        ].map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.href}>
            <Button fullWidth variant="outlined" onClick={() => navigate(item.href)}>
              {item.label}
            </Button>
          </Grid>
        ))}
        <Grid item xs={12} sm={6} md={4}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            disabled={!canActivate}
            title={!canActivate ? blockMessage : ""}
            onClick={() => {
              if (canActivate) {
                setActivateConfirmOpen(true);
              }
            }}
          >
            فعال‌سازی اجرا
          </Button>
        </Grid>
      </Grid>
      <Dialog open={activateConfirmOpen} onClose={() => setActivateConfirmOpen(false)}>
        <DialogTitle>تایید فعال‌سازی اجرا</DialogTitle>
        <DialogContent>
          <DialogContentText>
            با فعال‌سازی اجرا، پرونده وارد فاز اجرای پروژه می‌شود. این اقدام فقط پس از
            تایید نهایی، گیت وام (در صورت نیاز) و تسویه فاکتورهای الزامی مجاز است. ادامه
            می‌دهید؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateConfirmOpen(false)}>انصراف</Button>
          <Button
            variant="contained"
            onClick={() => {
              setActivateConfirmOpen(false);
              navigate(orchestratorLinks.executionActivation);
            }}
          >
            ادامه فعال‌سازی
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deferralConfirmOpen} onClose={() => setDeferralConfirmOpen(false)}>
        <DialogTitle>تأیید پرداخت پس از تحویل</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            با این تأیید، پرونده می‌تواند وارد فاز اجرا شود در حالی که مبلغ{" "}
            {formatMoney(gate?.postDeliveryPaymentAmount) || "پس از تحویل"} پس از تحویل پروژه
            دریافت خواهد شد.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="یادداشت کارشناس (اختیاری)"
            value={deferralNotes}
            onChange={(e) => setDeferralNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeferralConfirmOpen(false)} disabled={deferralSubmitting}>
            انصراف
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={deferralSubmitting}
            onClick={handleApprovePostDeliveryDeferral}
          >
            {deferralSubmitting ? "در حال ثبت..." : "تأیید"}
          </Button>
        </DialogActions>
      </Dialog>
      {!canActivate && gate ? (
        <Alert severity="warning">{blockMessage}</Alert>
      ) : null}
    </Stack>
  );
};

WorkspaceExecutionSection.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  source: PropTypes.string.isRequired,
  orchestratorLinks: PropTypes.object.isRequired,
  onProgressChanged: PropTypes.func,
  onGoToFinancial: PropTypes.func,
};

export default WorkspaceExecutionSection;
